import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import type {
  GroupListSerializer,
  InfluencerInviteDataSerializer,
  InfluencerInviteFilterSerializer,
  InviteInfluencerSerializer,
} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {create} from "@/store/createStore";
import {
  buildActiveFilters,
  captureSearchParamsDiffEvents,
  createDrawerActions,
  DEFAULT_FILTERS,
  DEFAULT_INITIAL_FILTERS,
  mergeFiltersPreservingCurrent,
  type TFilters,
  type TInitialFilters,
} from "@/store/filterStoreUtils";
import type {TActiveFilter, TInviteCategory, TNonFunctionKeys, TPaginated} from "@/store/types";
import {type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {debounce, sumBy, uniqBy, type DebouncedFuncLeading} from "lodash";
import qs from "qs";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TInviteFilters = TFilters & {
  ordering: TFilters["ordering"] | "-last_app_login";
};

type TCategorySelectionState = {
  allPossibleInfluencers: number[];
  noLimitInfluencers: number[];
  filters: TInviteFilters;
  searchParams: string | null;
};
type TUserFromTab = {
  userId: number;
  sourceTab: TInviteCategory;
  groupId?: number | null; // Only filled out when the influencer is selected by selecting the entire group
};

const initialFilterState = {
  followers__min: null,
  followers__max: null,
  engagement__min: null,
  engagement__max: null,
  rating__min: null,
  rating__max: null,
  tiktok_last_period_video_count__min: null,
  tiktok_last_period_video_count__max: null,
  followers__histogram: [],
  engagement__histogram: [],
  tiktok_last_period_video_count__histogram: [],
  rating__histogram: [],
  median_views__min: null,
  median_views__max: null,
  median_views__histogram: [],
  price__min: null,
  price__max: null,
  count: 0,
} as InfluencerInviteFilterSerializer;

const initialCategoryCounts = {
  ADMIN: 0,
  FAVORITE: 0,
  FOLLOWS_YOU: 0,
  GROUPS: 0,
  LIKED_YOU: 0,
  PRO_INFLUENCER: 0,
  RECOMMENDED: 0,
  WORKED_WITH: 0,
};

const initialSelectionState = {
  allPossibleInfluencers: [],
  noLimitInfluencers: [],
  selectedInfluencers: [],
  filters: {...DEFAULT_FILTERS},
  searchParams: null,
} as TCategorySelectionState;

type TState = {
  activeFilters: TActiveFilter[];
  categoryCounts: {[key in TInviteCategory]: number | null};
  categoryInitialCounts: {[key in TInviteCategory]: number | null};
  debouncedPendingSearchParams: DebouncedFuncLeading<() => void>;
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  defaultFilters: TInviteFilters;
  filterBoundaries: {
    [key in TInviteCategory]: Omit<InfluencerInviteFilterSerializer, "count">;
  };
  filters: TInviteFilters;
  initialFilters: TInitialFilters;
  invitableInfluencers: {[key in TInviteCategory]: TCategorySelectionState};
  isFilterDrawerOpen: boolean;
  isOpen: boolean;
  pendingSearchParams: string | null;
  savedFilters: TInviteFilters | null;
  searchParams: string | null;
  selectedCategory: TInviteCategory | null;
  selectedGroups: GroupListSerializer[];
  selectedInfluencers: TUserFromTab[];
  applyDrawerFilters(): void;
  buildSearchParams(): string;
  closeInviteModal(): void;
  deselectAll(): void;
  deselectAllInCategory(): void;
  getHistogram<
    K extends
      | "engagement__histogram"
      | "followers__histogram"
      | "median_views__histogram"
      | "rating__histogram"
      | "tiktok_last_period_video_count__histogram",
  >(
    key: K,
  ): TInitialFilters[K];
  getSelectedInfluencersCount(): number;
  inviteSelected(): Promise<void>;
  onCategoryChanged(newCategory: TInviteCategory): void;
  openDrawer(): void;
  resetAllFilters(): void;
  resetFilter(filterKeys: string[]): void;
  selectAllInCategory(): void;
  selectOrdering(): TInviteFilters["ordering"];
  setFilterBoundariesAndCounts(data: InfluencerInviteDataSerializer): void;
  setFilters<K extends keyof TInviteFilters, V extends TInviteFilters[K]>(
    key: K,
    value: V,
    options?: {skipQuery?: boolean},
  ): void;
  setMembersOfCategory(userIds: number[]): void;
  setNoLimitInfluencersOfCategory(userIds: number[]): void;
  setSearchParams(searchParams: string, options?: {skipAnalytics?: boolean}): void;
  setValue<K extends TNonFunctionKeys<TState>>(key: K, value: TState[K]): void;
  startDebouncedSearch(timeout?: number): void;
  toggleGroupInfluencerSelection(id: number, group: GroupListSerializer): void;
  toggleGroupSelection(group: GroupListSerializer): void;
  toggleInfluencerSelection(id: number, groupId?: number | null): void;
  updateActiveFilters(): void;
  updateInfluencerGroupId(id: number, groupId: number | null): void;
};

const initialState = {
  activeFilters: [],
  categoryCounts: initialCategoryCounts,
  categoryInitialCounts: initialCategoryCounts,
  defaultFilters: {...DEFAULT_FILTERS},
  filterBoundaries: {
    ADMIN: initialFilterState,
    FAVORITE: initialFilterState,
    FOLLOWS_YOU: initialFilterState,
    GROUPS: initialFilterState,
    LIKED_YOU: initialFilterState,
    PRO_INFLUENCER: initialFilterState,
    RECOMMENDED: initialFilterState,
    WORKED_WITH: initialFilterState,
  },
  filters: {...DEFAULT_FILTERS},
  initialFilters: {...DEFAULT_INITIAL_FILTERS},
  invitableInfluencers: {
    ADMIN: initialSelectionState,
    FAVORITE: initialSelectionState,
    FOLLOWS_YOU: initialSelectionState,
    GROUPS: initialSelectionState,
    LIKED_YOU: initialSelectionState,
    PRO_INFLUENCER: initialSelectionState,
    RECOMMENDED: initialSelectionState,
    WORKED_WITH: initialSelectionState,
  },
  isFilterDrawerOpen: false,
  isOpen: false,
  pendingSearchParams: null,
  savedFilters: null,
  searchParams: null,
  selectedCategory: null,
  selectedGroups: [],
  selectedInfluencers: [],
} as Pick<TState, TNonFunctionKeys<TState>>;

const mapBoundariesToHistogramRanges = (
  boundaries: Omit<InfluencerInviteFilterSerializer, "count">,
): TInitialFilters => ({
  ...DEFAULT_INITIAL_FILTERS,
  engagement__histogram: boundaries.engagement__histogram ?? [],
  engagement__max: boundaries.engagement__max ?? 0,
  engagement__min: boundaries.engagement__min ?? 0,
  followers__histogram: boundaries.followers__histogram ?? [],
  followers__max: boundaries.followers__max ?? 0,
  followers__min: boundaries.followers__min ?? 0,
  median_views__histogram: boundaries.median_views__histogram ?? [],
  median_views__max: boundaries.median_views__max ?? 0,
  median_views__min: boundaries.median_views__min ?? 0,
  rating__histogram: boundaries.rating__histogram ?? [],
  rating__max: boundaries.rating__max ?? DEFAULT_INITIAL_FILTERS.rating__max,
  rating__min: boundaries.rating__min ?? DEFAULT_INITIAL_FILTERS.rating__min,
  tiktok_last_period_video_count__histogram: boundaries.tiktok_last_period_video_count__histogram ?? [],
  tiktok_last_period_video_count__max: boundaries.tiktok_last_period_video_count__max ?? 0,
  tiktok_last_period_video_count__min: boundaries.tiktok_last_period_video_count__min ?? 0,
});

const buildDefaultFilterValues = (ranges: Omit<InfluencerInviteFilterSerializer, "count">): TInviteFilters => ({
  ...DEFAULT_FILTERS,
  engagement_end: ranges.engagement__max ?? 0,
  engagement_start: ranges.engagement__min ?? 0,
  followers_end: ranges.followers__max ?? 0,
  followers_start: ranges.followers__min ?? 0,
  median_views_end: ranges.median_views__max ?? 0,
  median_views_start: ranges.median_views__min ?? 0,
  rating_end: ranges.rating__max ?? DEFAULT_FILTERS.rating_end,
  rating_start: ranges.rating__min ?? DEFAULT_FILTERS.rating_start,
  tiktok_last_period_video_count_end: ranges.tiktok_last_period_video_count__max ?? 0,
  tiktok_last_period_video_count_start: ranges.tiktok_last_period_video_count__min ?? 0,
});

const useInfluencerInviteStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        debouncedPendingSearchParams: debounce(() => {
          const pendingParams = get().buildSearchParams();

          set(
            state => {
              state.pendingSearchParams = pendingParams;
            },
            false,
            "useInfluencerInviteStore/setPendingSearchParams",
          );
        }, 1000),
        debouncedSearchParams: debounce(() => {
          get().buildSearchParams();
        }, 1000),
        setValue: (key, value) =>
          set(
            state => {
              state[key] = value;
            },
            false,
            "useInfluencerInviteStore/setValue",
          ),
        deselectAll: () =>
          set(
            state => {
              state.selectedInfluencers = [];
              state.selectedGroups = [];
            },
            false,
            "useInfluencerInviteStore/deselectAll",
          ),
        selectAllInCategory: () => {
          set(
            state => {
              state.selectedInfluencers = uniqBy(
                state.selectedInfluencers.concat(
                  state.invitableInfluencers[get().selectedCategory!].allPossibleInfluencers.map(id => ({
                    userId: id,
                    sourceTab: get().selectedCategory!,
                  })),
                ),
                "userId",
              );
            },
            false,
            "useInfluencerInviteStore/selectAllInCategory",
          );
        },
        deselectAllInCategory: () =>
          set(
            state => {
              state.selectedInfluencers = state.selectedInfluencers.filter(
                (userData: TUserFromTab) =>
                  !state.invitableInfluencers[state.selectedCategory!].allPossibleInfluencers.includes(userData.userId),
              );
            },
            true,
            "useInfluencerInviteStore/deselectAllInCategory",
          ),
        buildSearchParams: () => {
          const filters = get().filters;

          const serializedParams = {
            ...filters,
            audience_country: filters.audience_country.join(","),
            audience_location_search: filters.audience_location_search.map(item => item.name).join(","),
            cutoff_value: filters.vibe ? filters.cutoff_value : undefined,
            influencer_country: filters.influencer_country.join(","),
            maximum_cutoff_value: filters.vibe ? filters.maximum_cutoff_value : undefined,
            feed_hashtags:
              filters.is_competitor_filter_active && filters.feed_hashtags.length > 0
                ? filters.feed_hashtags.join(",")
                : undefined,
            feed_hashtags_mode:
              filters.is_competitor_filter_active && filters.feed_hashtags.length > 0
                ? filters.feed_hashtags_mode
                : undefined,
            feed_tags:
              filters.is_competitor_filter_active && filters.feed_tags.length > 0
                ? filters.feed_tags.join(",")
                : undefined,
            feed_tags_mode:
              filters.is_competitor_filter_active && filters.feed_tags.length > 0 ? filters.feed_tags_mode : undefined,
            location_radius_km:
              filters.location_radius_km > 0 && filters.location_search.some(item => item.type !== "region")
                ? filters.location_radius_km
                : undefined,
            location_search: filters.location_search
              .filter(item => item.type !== "zip")
              .map(item => item.name)
              .join(","),
            location_zips: filters.location_search
              .filter(item => item.type === "zip")
              .map(item => `${item.country_code}:${item.name}`)
              .join(","),
          };

          return qs.stringify(serializedParams, {arrayFormat: "comma"});
        },
        setFilters: (key, value, options = {skipQuery: false}) => {
          set(
            state => {
              state.filters[key] = value;

              if (key !== "cutoff_value" && key !== "maximum_cutoff_value" && key !== "vibe") {
                state.filters.cutoff_value = DEFAULT_FILTERS.cutoff_value;
                state.filters.maximum_cutoff_value = DEFAULT_FILTERS.maximum_cutoff_value;
              }
            },
            false,
            "useInfluencerInviteStore/setFilters",
          );

          get().updateActiveFilters();

          if (options.skipQuery) {
            return;
          }

          if (get().isFilterDrawerOpen) {
            get().debouncedPendingSearchParams();
            return;
          }

          get().startDebouncedSearch();
        },
        setSearchParams: (searchParams, options) => {
          const previous = get().searchParams;
          set(
            state => {
              state.searchParams = searchParams;
            },
            false,
            "useInfluencerInviteStore/setSearchParams",
          );
          if (!options?.skipAnalytics) {
            captureSearchParamsDiffEvents(previous, searchParams, "invite");
          }
        },
        setMembersOfCategory: userIds =>
          set(
            state => {
              state.invitableInfluencers[state.selectedCategory!].allPossibleInfluencers = userIds;
            },
            false,
            "useInfluencerInviteStore/setMembersOfCategory",
          ),
        setNoLimitInfluencersOfCategory: userIds =>
          set(
            state => {
              state.invitableInfluencers[state.selectedCategory!].noLimitInfluencers = userIds;
            },
            false,
            "useInfluencerInviteStore/setNoLimitInfluencersOfCategory",
          ),

        setFilterBoundariesAndCounts: data => {
          set(
            state => {
              const counts = {
                ADMIN: data.all_invitable_influencers.count,
                FAVORITE: data.favourite_influencers.count,
                FOLLOWS_YOU: data.influencers_following_you.count,
                GROUPS: data.groups.count,
                LIKED_YOU: data.liked_your_campaign.count,
                PRO_INFLUENCER: data.all_invitable_pro_influencers_data.count,
                RECOMMENDED: data.recommended_influencers.count,
                WORKED_WITH: data.previously_worked_with.count,
              };
              state.categoryCounts = counts;
              state.categoryInitialCounts = counts;
              state.filterBoundaries = {
                ADMIN: data.all_invitable_influencers,
                FAVORITE: data.favourite_influencers,
                FOLLOWS_YOU: data.influencers_following_you,
                GROUPS: get().filterBoundaries.GROUPS,
                LIKED_YOU: data.liked_your_campaign,
                PRO_INFLUENCER: data.all_invitable_pro_influencers_data,
                RECOMMENDED: data.recommended_influencers,
                WORKED_WITH: data.previously_worked_with,
              };
              let k: TInviteCategory;
              for (k in state.filterBoundaries) {
                const defaults = buildDefaultFilterValues(state.filterBoundaries[k]);

                if (k === state.selectedCategory) {
                  state.filters = defaults;
                } else {
                  state.invitableInfluencers[k].filters = defaults;
                }
              }

              if (state.selectedCategory) {
                const boundaries = state.filterBoundaries[state.selectedCategory];
                state.initialFilters = mapBoundariesToHistogramRanges(boundaries);
                state.defaultFilters = buildDefaultFilterValues(boundaries);
              }
            },
            false,
            "useInfluencerInviteStore/setFilterBoundariesAndCounts",
          );

          get().updateActiveFilters();
        },
        inviteSelected: async () => {
          const campaignId = useCampaignManagementStore.getState().campaign.id;

          // First we invite the individual influencers
          await client.post(CAMPAIGN_KEYS.mCampaignBulkInvite(campaignId)[0], {
            invites: get()
              .selectedInfluencers.filter(i => !i.groupId)
              .map(i => `${i.userId}:${i.sourceTab}`),
          });

          // Then we invite the groups that are fully selected (the backend will ignore influencers that are already invited by the first API call)
          // TODO: refactor this into a single API call when the backend supports multiple group_id parameters at the same time
          await Promise.all(
            get().selectedGroups.map(g =>
              client.post(CAMPAIGN_KEYS.mCampaignBulkInvite(campaignId)[0], {group_id: g.id}),
            ),
          );
        },
        onCategoryChanged: newCategory => {
          if (newCategory === get().selectedCategory) return;
          get().debouncedPendingSearchParams?.cancel();

          const currentFilters = get().filters;
          const competitorFields = {
            feed_hashtags: currentFilters.feed_hashtags,
            feed_hashtags_mode: currentFilters.feed_hashtags_mode,
            feed_tags: currentFilters.feed_tags,
            feed_tags_mode: currentFilters.feed_tags_mode,
            is_competitor_filter_active: currentFilters.is_competitor_filter_active,
          };

          set(
            state => {
              const oldCategory = state.selectedCategory;
              if (oldCategory) {
                state.invitableInfluencers[oldCategory].filters = {...state.filters};
                state.invitableInfluencers[oldCategory].searchParams = get().buildSearchParams();
              }

              state.isOpen = true;
              state.isFilterDrawerOpen = false;
              state.selectedCategory = newCategory;
              state.pendingSearchParams = null;
              state.savedFilters = null;
              state.filters = {...state.invitableInfluencers[newCategory].filters, ...competitorFields};
              state.searchParams = state.invitableInfluencers[newCategory].searchParams;

              const boundaries = state.filterBoundaries[newCategory];
              state.initialFilters = mapBoundariesToHistogramRanges(boundaries);
              state.defaultFilters = buildDefaultFilterValues(boundaries);
            },
            false,
            "useInfluencerInviteStore/onCategoryChanged",
          );

          get().updateActiveFilters();
          get().startDebouncedSearch(0);
        },
        toggleInfluencerSelection: (id, groupId) => {
          if (
            get()
              .selectedInfluencers.map(u => u.userId)
              .includes(id)
          )
            set(
              state => {
                state.selectedInfluencers = state.selectedInfluencers.filter(item => item.userId !== id);
              },
              false,
              "useInfluencerInviteStore/toggleInfluencerSelection",
            );
          else
            set(
              state => {
                state.selectedInfluencers.push({
                  userId: id,
                  sourceTab: get().selectedCategory!,
                  groupId,
                });
              },
              false,
              "useInfluencerInviteStore/toggleInfluencerSelection",
            );
        },
        toggleGroupSelection: group => {
          const groupIndex = get().selectedGroups.findIndex(g => g.id === group.id);

          if (groupIndex !== -1) {
            set(
              state => {
                state.selectedGroups = state.selectedGroups.filter(g => g.id !== group.id);
              },
              false,
              "useInfluencerInviteStore/toggleGroupSelection",
            );
          } else {
            set(
              state => {
                state.selectedGroups.push(group);
              },
              false,
              "useInfluencerInviteStore/toggleGroupSelection",
            );
          }
        },
        toggleGroupInfluencerSelection: (id, group) => {
          if (!!get().selectedGroups.find(g => g.id === group.id)) {
            get().toggleGroupSelection(group);
          }

          get().toggleInfluencerSelection(id);
        },
        updateInfluencerGroupId: (id, groupId) => {
          set(
            state => {
              state.selectedInfluencers = get().selectedInfluencers.map(i => (i.userId === id ? {...i, groupId} : i));
            },
            false,
            "useInfluencerInviteStore/updateInfluencerGroupId",
          );
        },
        getHistogram: key => {
          const boundaries = get().filterBoundaries[get().selectedCategory!];
          return mapBoundariesToHistogramRanges(boundaries)[key];
        },
        closeInviteModal: () => {
          ["LIKED_YOU", "WORKED_WITH", "FAVORITE", "FOLLOWS_YOU", "RECOMMENDED"].map(key =>
            queryClient.removeQueries({queryKey: [key]}),
          );

          set(
            state => {
              let k: TInviteCategory;
              for (k in state.filterBoundaries) {
                state.invitableInfluencers[k].filters = buildDefaultFilterValues(state.filterBoundaries[k]);
              }
              state.categoryCounts = {...state.categoryInitialCounts};
              state.selectedInfluencers = [];
              state.selectedGroups = [];
              state.isOpen = false;
              state.selectedCategory = null;
              state.activeFilters = [];
              state.filters = {...DEFAULT_FILTERS};
              state.defaultFilters = {...DEFAULT_FILTERS};
              state.initialFilters = {...DEFAULT_INITIAL_FILTERS};
              state.isFilterDrawerOpen = false;
              state.pendingSearchParams = null;
              state.savedFilters = null;
              state.searchParams = null;
            },
            false,
            "closeInviteModal/reset",
          );
        },
        startDebouncedSearch: (timeout = 1000) => {
          get().debouncedSearchParams.cancel();

          const searchParams = get().buildSearchParams();
          const cachedQuery = queryClient.getQueryData<
            InfiniteData<AxiosResponse<TPaginated<InviteInfluencerSerializer>>>
          >(
            CAMPAIGN_KEYS.qCampaignInvitableInfluencers({
              campaignId: Number(useCampaignManagementStore.getState().campaign.id),
              category: get().selectedCategory!,
              searchParams,
            }),
          );

          if (!!cachedQuery?.pages) {
            timeout = 0;
          }

          set(
            state => {
              state.debouncedSearchParams = debounce(() => {
                get().setSearchParams(searchParams);
              }, timeout);
            },
            false,
            "useInfluencerInviteStore/setDebouncedSearchParams",
          );

          get().debouncedSearchParams();
        },
        getSelectedInfluencersCount: () => {
          const influencerCount = get().selectedInfluencers.filter(i => !i.groupId).length;
          const groupInfluencerCount = sumBy(get().selectedGroups, g => g.verified_influencers_count ?? 0);

          return influencerCount + groupInfluencerCount;
        },
        selectOrdering: () => get().filters.ordering,
        updateActiveFilters: () => {
          const filters = get().filters;
          const initialFilters = get().initialFilters;
          const active = buildActiveFilters(filters, initialFilters);

          set(
            state => {
              state.activeFilters = active;
            },
            false,
            "useInfluencerInviteStore/updateActiveFilters",
          );
        },
        resetAllFilters: () => {
          if (!get().selectedCategory) return;

          const current = get().filters;
          set(
            state => {
              state.filters = mergeFiltersPreservingCurrent(current, state.defaultFilters);
            },
            false,
            "useInfluencerInviteStore/resetAllFilters",
          );

          get().updateActiveFilters();

          if (get().isFilterDrawerOpen) {
            get().debouncedPendingSearchParams();
          } else {
            get().debouncedSearchParams.cancel();
            get().setSearchParams(get().buildSearchParams(), {skipAnalytics: true});
          }
        },
        resetFilter: filterKeys => {
          if (!get().selectedCategory) return;

          set(
            state => {
              const patch = Object.fromEntries(
                filterKeys.map(key => [key, state.defaultFilters[key as keyof TInviteFilters]]),
              );
              Object.assign(state.filters, patch);
            },
            false,
            "useInfluencerInviteStore/resetFilter",
          );

          get().updateActiveFilters();
          get().debouncedPendingSearchParams();
        },
        ...createDrawerActions(get, set, "useInfluencerInviteStore"),
      }),
      {
        name: "useInfluencerInviteStore",
      },
    ),
  ),
);

export default useInfluencerInviteStore;
